import React, { useEffect } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  OutlinedInput,
  FormHelperText,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useErrorMessage } from '@hooks/use-error-message.ts';
import { validateNotOnlySpaces } from '@utils/trim-form-values-utils.ts';
import { mediaLibraryErrorMessages } from '@utils/media-library-utils';
import useMediaLibraryStore from '@store/media-library-store/media-library.store.ts';
import { validateFolderNameServer } from '@validators/validators.ts';

const AddFolderContent: React.FC<{
  onSave: (data: { name: string }) => void;
  updateFolderData: any | null;
}> = ({ onSave, updateFolderData }) => {
  const { t } = useTranslation();
  const { currentFolder, isFolderLoading } = useMediaLibraryStore();
  const getErrorMessage = useErrorMessage();

  const {
    register,
    formState: { errors, isValid, isValidating },
    handleSubmit,
    reset
  } = useForm<any>({ mode: 'onChange' });

  useEffect(() => {
    if (updateFolderData) {
      reset({ name: updateFolderData?.title });
    }
  }, [updateFolderData, reset]);

  const onSubmit = (data: { name: string }) => {
    onSave({
      name: data.name
    });
  };

  return (
    <div className="mt-4">
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-1">
        <div className="flex justify-between gap-12">
          <div className="w-full flex flex-col">
            <FormControl error={!!errors.name}>
              <FormLabel className="flex">{t('label_name')}</FormLabel>
              <OutlinedInput
                autoComplete={'off'}
                {...register('name', {
                  required: t('field_required'),
                  validate: {
                    notOnlySpaces: validateNotOnlySpaces,
                    nameExists: async (value) => {
                      return await validateFolderNameServer(
                        value,
                        currentFolder!.id
                      );
                    }
                  }
                })}
                endAdornment={
                  isValidating ? (
                    <InputAdornment position="end">
                      <CircularProgress size={16} />
                    </InputAdornment>
                  ) : null
                }
                error={!!errors.name}
                placeholder={t('placeholder_name')}
              />
              <FormHelperText>
                {errors?.name &&
                  getErrorMessage({
                    errors,
                    fieldName: 'name',
                    errorMessages: mediaLibraryErrorMessages
                  })}
              </FormHelperText>
            </FormControl>
          </div>
        </div>

        <div className="flex justify-end mt-6 sticky pb-1 bottom-0 bg-white">
          <Button
            type="submit"
            variant="contained"
            loading={isFolderLoading()}
            disabled={!isValid || isValidating}
          >
            {t('save')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddFolderContent;
